import React, { useEffect, useState } from "react";
import {
  Box,
  Flex,
  Text,
  Icon,
  useColorModeValue,
  SimpleGrid,
  VStack,
  HStack,
  Button,
  Divider,
  Badge,
  Input,
  Select,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  useToast,
  Image,
} from "@chakra-ui/react";
import {
  MdEvStation,
  MdArrowBack,
  MdBolt,
  MdStop,
  MdTimer,
  MdAttachMoney,
} from "react-icons/md";
import ccstype2 from "assets/img/chargePointsTab/ccstype2.png";
import AC1 from "assets/img/chargePointsTab/actype1.png";
import AC2 from "assets/img/chargePointsTab/ac2.png";
import CDMO from "assets/img/chargePointsTab/chademo.png";
import DOMESTIC_D from "assets/img/chargePointsTab/domestic_d.png";
import useAxios from "axios-hooks";
import { useNavigate, useParams } from "react-router-dom";
import LoadingSpinner from "components/feedback/LoadingSpinner";
import OngoingSession from "./OngoingSession";
import ChargingControls from "./ChargingControls";

const ChargerDetailsScreen = () => {
  const { stationId } = useParams();
  const navigate = useNavigate();
  const [station, setStation] = useState(null);
  const [connectors, setConnectors] = useState([]);
  const [selectedConnector, setSelectedConnector] = useState(null);
  const [mode, setMode] = useState("money");
  const [value, setValue] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [errors, setErrors] = useState({});
  const [sessionData, setSessionData] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const toast = useToast();

  const bgColor = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const textColor = useColorModeValue("gray.800", "white");
  const labelColor = useColorModeValue("gray.600", "gray.400");

  const [{ data: stationData, loading: stationLoading }, getStation] = useAxios(
    { method: "GET", url: `/api/stations/${stationId}` },
    { manual: true }
  );

  const [{ data: connectorsData, loading: connectorsLoading }, getConnectors] =
    useAxios(
      { method: "GET", url: `/api/evses/forStation/${stationId}` },
      { manual: true }
    );

  const [{ data: sessionResponse }, getSession] = useAxios(
    {
      method: "GET",
      url: selectedConnector
        ? `/api/chargingSession/ongoing?evseUid=${selectedConnector.EvseUid}`
        : null,
    },
    { manual: true }
  );

  const [{ loading: stopLoading }, executeStop] = useAxios(
    { method: "POST", url: `/api/ocpp16/charging/stop` },
    { manual: true }
  );

  const fetchData = async () => {
    try {
      const [stationRes, connectorsRes] = await Promise.all([
        getStation(),
        getConnectors(),
      ]);
      if (isInitialLoad) {
        setIsInitialLoad(false);
      }
      setStation(stationRes.data.data);
      const connectorsData = connectorsRes.data.data || [];
      setConnectors(connectorsData);
      // Set first connector as default selected
      if (connectorsData.length > 0) {
        setSelectedConnector(connectorsData[0]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    if (stationId) {
      fetchData();
    }
  }, [stationId]);

  // Poll for station data every 10 seconds
  useEffect(() => {
    let interval;
    if (stationId) {
      interval = setInterval(async () => {
        try {
          const stationRes = await getStation();
          setStation(stationRes.data.data);
        } catch (error) {
          console.error("Error polling station data:", error);
        }
      }, 17000);
    }
    return () => clearInterval(interval);
  }, [stationId]);

  // Poll for connector data every 6 seconds
  useEffect(() => {
    let interval;
    if (stationId) {
      interval = setInterval(async () => {
        try {
          const connectorsRes = await getConnectors();
          const connectorsData = connectorsRes.data.data || [];
          setConnectors(connectorsData);
          // Update selected connector with fresh data
          if (selectedConnector) {
            const updatedConnector = connectorsData.find(
              (c) => c.Id === selectedConnector.Id
            );
            if (updatedConnector) {
              setSelectedConnector(updatedConnector);
            }
          }
        } catch (error) {
          console.error("Error polling connector data:", error);
        }
      }, 13000);
    }
    return () => clearInterval(interval);
  }, [stationId, selectedConnector?.Id]);

  // Poll for session data every 4 seconds when connector is Charging
  useEffect(() => {
    if (
      selectedConnector?.Status === "Charging" &&
      selectedConnector?.EvseUid
    ) {
      fetchSession();
      const interval = setInterval(fetchSession, 7000);
      return () => clearInterval(interval);
    } else {
      setSessionData(null);
    }
  }, [selectedConnector?.Status, selectedConnector?.EvseUid]);

  const fetchSession = async () => {
    try {
      const response = await getSession();
      const data = response.data.data;
      setSessionData(data);
    } catch (error) {
      console.error("Error fetching session:", error);
      setSessionData(null);
    }
  };

  const [{ loading: startLoading }, startChargingAPI] = useAxios(
    { method: "POST", url: `/api/ocpp16/charging/start` },
    { manual: true }
  );

  const validateInputs = () => {
    const newErrors = {};

    if (!selectedConnector) {
      newErrors.connector = "Please select a connector";
    }

    if (!mobileNumber || mobileNumber.trim() === "") {
      newErrors.mobileNumber = "Mobile number is required";
    } else if (!/^\d{10}$/.test(mobileNumber)) {
      newErrors.mobileNumber = "Mobile number must be exactly 10 digits";
    }

    if (!value || value === "" || parseFloat(value) <= 0) {
      newErrors.value = "Value must be greater than 0";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleStart = async () => {
    if (!validateInputs()) {
      return;
    }

    const body = {
      EvseSerialId: selectedConnector.EvseSerialId,
      StationId: selectedConnector.EvseId,
      IdTag: mobileNumber.trim(),
      MobileNumber: mobileNumber.trim(),
      Mode: mode,
      Value: parseFloat(value),
    };

    try {
      const response = await startChargingAPI({ data: body });
      toast({
        title: "Charging Started Successfully",
        description:
          response.data.data?.Message || "Charging session has been initiated",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      // Reset form
      setValue("");
      setMobileNumber("");
      setErrors({});
    } catch (error) {
      const err = error?.response?.data?.error;
      toast({
        title: "Error Starting Charger",
        description:
          err || "An error occurred while starting the charging session",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleStopCharging = async () => {
    if (!sessionData) return;

    const body = {
      StationId: selectedConnector.EvseId,
      TransactionId: sessionData.Id,
    };

    try {
      await executeStop({ data: body });
      toast({
        title: "Session Stopped",
        description: "Charging session stopped successfully",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      // Navigate to session summary after stopping
      setTimeout(() => {
        navigate(`/admin/charging_management/summary/${sessionData.Id}`);
      }, 1500);
    } catch (error) {
      toast({
        title: "Error Stopping Session",
        description: error.response?.data?.error || "Something went wrong",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const calculateDuration = () => {
    if (!sessionData?.StartTime) return "0h 0m";

    const startTime = new Date(sessionData.StartTime);
    const now = new Date();
    const diffMs = now - startTime;
    const diffSeconds = Math.floor(diffMs / 1000);

    const hours = Math.floor(diffSeconds / 3600);
    const minutes = Math.floor((diffSeconds % 3600) / 60);

    return `${hours}h ${minutes}m`;
  };

  const getConnectorImage = (connector) => {
    const connectorType =
      connector?.Connectors?.[0]?.Standard || connector?.ConnectorType;

    switch (connectorType) {
      case "AC Type 1":
        return { img: AC1, name: "AC Type 1" };
      case "AC Type 2":
        return { img: AC2, name: "AC Type 2" };
      case "CCS Type 2":
        return { img: ccstype2, name: "CCS Type 2" };
      case "CHAdeMO":
        return { img: CDMO, name: "CHAdeMO" };
      case "DOMESTIC_D":
        return { img: DOMESTIC_D, name: "3 Pin Socket (16A - 230V)" };
      default:
        return { img: ccstype2, name: connectorType || "CCS Type 2" };
    }
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case "Charging":
        return {
          color: "white",
          bg: "linear-gradient(135deg, #23A979 0%, #ff9966 100%)",
        };
      case "Preparing":
        return {
          color: "white",
          bg: "linear-gradient(135deg, #f5a623 0%, #f7b955 100%)",
        };
      case "Available":
        return {
          color: "white",
          bg: "linear-gradient(135deg, #48BB78 0%, #68D391 100%)",
        };
      default:
        return {
          color: "white",
          bg: "linear-gradient(135deg, #718096 0%, #A0AEC0 100%)",
        };
    }
  };

  const isStartButtonEnabled = () => {
    if (!selectedConnector) return false;
    if (
      selectedConnector.Status !== "Available" &&
      selectedConnector.Status !== "Preparing"
    )
      return false;
    if (!mobileNumber || !/^\d{10}$/.test(mobileNumber)) return false;
    if (!value || parseFloat(value) <= 0) return false;
    return true;
  };

  if ((stationLoading || connectorsLoading) && isInitialLoad) {
    return (
      <Box pt={{ base: 40, md: 10 }}>
        <LoadingSpinner />
      </Box>
    );
  }

  if (!station) {
    return (
      <Box bg={bgColor} pt={{ base: 40, md: 10 }} px={{ base: 2, md: 4 }}>
        <Text>Station not found</Text>
      </Box>
    );
  }

  return (
    <Box bg={bgColor} pt={{ base: 40, md: 10 }} px={{ base: 2, md: 4 }}>
      <VStack spacing={6} align="stretch">
        {/* Back Button */}
        <Flex justify="flex-start">
          <Button
            leftIcon={<MdArrowBack />}
            bgColor="brand.500"
            _hover={{ bgColor: "#d44e00" }}
            onClick={() => navigate(-1)}
            color="white"
            px={{ base: 4, md: 5 }}
            py={{ base: 2, md: 3 }}
            fontSize={{ base: "sm", md: "md" }}
            borderRadius={8}
          >
            Back
          </Button>
        </Flex>

        {/* Charger Details Card */}
        <Box
          bg={cardBg}
          borderRadius="xl"
          border="1px solid"
          borderColor={borderColor}
          p={{ base: 3, md: 6 }}
          boxShadow="sm"
          overflow="hidden"
        >
          <VStack align="stretch" spacing={{ base: 2, md: 4 }}>
            <Flex
              align="center"
              gap={{ base: 2, md: 3 }}
              justify="space-between"
              overflow="hidden"
            >
              <Flex align="center" gap={{ base: 2, md: 3 }} flex="1" minW="0">
                <Icon
                  as={MdEvStation}
                  boxSize={{ base: 5, md: 6 }}
                  color="brand.500"
                  flexShrink={0}
                />
                <Text
                  fontSize={{ base: "lg", md: "xl", lg: "2xl" }}
                  fontWeight="bold"
                  color={textColor}
                  overflow="hidden"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                >
                  {station.Name || station.StationId}
                </Text>
              </Flex>
              <Badge
                colorScheme={station.Status === "Online" ? "green" : "red"}
                fontSize={{ base: "xs", md: "md" }}
                px={{ base: 2, md: 3 }}
                py={{ base: 0.5, md: 1 }}
                borderRadius="md"
                flexShrink={0}
              >
                {station.Status || "Offline"}
              </Badge>
            </Flex>

            <Divider />

            <Flex
              justify="space-between"
              align="start"
              flexWrap="wrap"
              gap={{ base: 2, md: 4 }}
            >
              <VStack align="stretch" spacing={1}>
                <Text
                  fontSize={{ base: "2xs", md: "xs" }}
                  color={labelColor}
                  fontWeight="semibold"
                >
                  Power
                </Text>
                <Text fontSize={{ base: "sm", md: "md" }} fontWeight="medium">
                  {station.MaxElectricPower || "0"} kWh
                </Text>
              </VStack>

              <VStack align="stretch" spacing={1}>
                <Text
                  fontSize={{ base: "2xs", md: "xs" }}
                  color={labelColor}
                  fontWeight="semibold"
                >
                  Type
                </Text>
                <Text fontSize={{ base: "sm", md: "md" }} fontWeight="medium">
                  {station.PowerType || "DC"}
                </Text>
              </VStack>
            </Flex>
          </VStack>
        </Box>

        {/* Connectors Flex Grid */}
        <Flex flexWrap="wrap" gap={3} justify="flex-start">
          {connectors.map((connector, index) => {
            const connectorInfo = getConnectorImage(connector);
            const statusConfig = getStatusConfig(connector.Status);

            return (
              <Box
                key={connector.Id || index}
                bg={
                  selectedConnector?.Id === connector.Id
                    ? "rgba(255, 92, 0, 0.1)"
                    : cardBg
                }
                borderRadius="lg"
                border="1px solid"
                borderColor={borderColor}
                p={{ base: 2, md: 3 }}
                boxShadow="sm"
                cursor="pointer"
                onClick={() => setSelectedConnector(connector)}
                transition="all 0.2s"
                flex={{
                  base: "0 0 calc(50% - 8px)",
                  lg: "0 0 calc(33.333% - 12px)",
                }}
                minW={{ base: "calc(50% - 8px)", lg: "calc(33.333% - 12px)" }}
                _hover={{
                  bg: "rgba(255, 92, 0, 0.08)",
                }}
                overflow="hidden"
              >
                <Flex align="center" gap={{ base: 2, md: 3 }} overflow="hidden">
                  {/* Connector Image - Left */}
                  <Box
                    p={{ base: 1.5, md: 2 }}
                    borderRadius="md"
                    border="1px solid"
                    borderColor="rgba(35, 169, 121, 0.3)"
                    bg="linear-gradient(135deg, rgba(35, 169, 121, 0.08) 0%, rgba(255, 153, 102, 0.05) 100%)"
                    flexShrink={0}
                  >
                    <Image
                      src={connectorInfo.img}
                      alt={connectorInfo.name}
                      w={{ base: "40px", md: "50px" }}
                      h={{ base: "40px", md: "50px" }}
                      objectFit="contain"
                    />
                  </Box>

                  {/* Type and Status - Right */}
                  <VStack align="stretch" spacing={{ base: 1, md: 2 }} flex={1} minW="0">
                    {/* Gun Label */}
                    <Text
                      fontSize={{ base: "2xs", sm: "xs" }}
                      fontWeight="700"
                      color="#FF5C00"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      whiteSpace="nowrap"
                    >
                      Gun {connector.ConnectorId || index + 1}
                    </Text>

                    {/* Connector Type Name */}
                    <Text
                      fontSize={{ base: "2xs", sm: "xs" }}
                      fontWeight="600"
                      color={textColor}
                      overflow="hidden"
                      textOverflow="ellipsis"
                      whiteSpace="nowrap"
                    >
                      {connectorInfo.name}
                    </Text>

                    {/* Status Badge */}
                    <Badge
                      fontSize={{ base: "2xs", sm: "xs" }}
                      px={{ base: 1, sm: 2 }}
                      py={{ base: 0.5, sm: 1 }}
                      borderRadius="md"
                      bg={statusConfig.bg}
                      color={statusConfig.color}
                      textAlign="center"
                    >
                      {connector.Status || "Unknown"}
                    </Badge>
                  </VStack>
                </Flex>
              </Box>
            );
          })}
        </Flex>

        {/* Charging Session Details or Controls */}
        {selectedConnector?.Status === "Charging" && sessionData ? (
          <OngoingSession
            sessionData={sessionData}
            station={station}
            selectedConnector={selectedConnector}
            handleStopCharging={handleStopCharging}
            stopLoading={stopLoading}
            calculateDuration={calculateDuration}
          />
        ) : (
          <ChargingControls
            mode={mode}
            setMode={setMode}
            value={value}
            setValue={setValue}
            mobileNumber={mobileNumber}
            setMobileNumber={setMobileNumber}
            errors={errors}
            setErrors={setErrors}
            selectedConnector={selectedConnector}
            handleStart={handleStart}
            isStartButtonEnabled={isStartButtonEnabled}
            startLoading={startLoading}
          />
        )}
      </VStack>
    </Box>
  );
};

export default ChargerDetailsScreen;
